import type { ShipNeedAnalysisCount } from "../ShipNeedAnalysisPage";

interface FunnelItem {
    title: string;
    count: number;
    color: string;
    background: string;
    rate?: string;
}

/** 保留旧 Vue 漏斗图的转化率口径：无有效分子或分母时显示 N/A。 */
const calculateRate = (count: number, total: number) => {
    if (!count || !total) return "N/A";
    return `${((count / total) * 100).toFixed(2)}%`;
};

/** 将统计卡片数据转换为旧 Vue 漏斗图的四层指标。 */
const getFunnelItems = (cardData: ShipNeedAnalysisCount): FunnelItem[] => {
    const total = cardData.totalNum || 0;
    const valid = total - (cardData.cancelNum || 0) - (cardData.waitNum || 0);
    const validBase = total - (cardData.cancelNum || 0) - (cardData.closeNum || 0) - (cardData.waitNum || 0);
    const quote = cardData.quoteNum || 0;
    const generate = cardData.generateNum || 0;

    return [
        { title: "需求总数", count: total, color: "#3370FF", background: "#D8E7FF" },
        {
            title: "有效需求数",
            count: valid || 0,
            rate: calculateRate(valid, total),
            color: "#0997FF",
            background: "#E3F3FF"
        },
        {
            title: "被报价需求数",
            count: quote,
            rate: calculateRate(quote, validBase),
            color: "#0BBB7D",
            background: "#E9FFFB"
        },
        {
            title: "生成运单数",
            count: generate,
            rate: calculateRate(generate, quote),
            color: "#F85C25",
            background: "#FFF6F3"
        }
    ];
};

export function ShipNeedConversionFunnel({ cardData }: { cardData: ShipNeedAnalysisCount }) {
    const items = getFunnelItems(cardData);

    return (
        <div className="flex flex-wrap items-center gap-8 px-4 py-8" data-testid="ship-need-conversion-funnel">
            <svg
                width="310"
                height="285"
                viewBox="0 0 310 285"
                role="img"
                aria-label="船运需求转化漏斗图"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M12 18h286l-8 48H20z" fill="#D8E7FF" stroke="#3370FF" />
                <path d="M35 70h240l-15 64H50z" fill="#E3F3FF" stroke="#0997FF" />
                <path d="M58 136h194l-18 62H76z" fill="#E9FFFB" stroke="#0BBB7D" />
                <path d="M82 200h146l-16 62H98z" fill="#FFF6F3" stroke="#F85C25" />
                <text x="155" y="48" textAnchor="middle" fill="#3370FF" fontSize="24" fontWeight="700">
                    {items[0].count}
                </text>
                <text x="155" y="110" textAnchor="middle" fill="#0997FF" fontSize="24" fontWeight="700">
                    {items[1].count}
                </text>
                <text x="155" y="174" textAnchor="middle" fill="#0BBB7D" fontSize="24" fontWeight="700">
                    {items[2].count}
                </text>
                <text x="155" y="238" textAnchor="middle" fill="#F85C25" fontSize="24" fontWeight="700">
                    {items[3].count}
                </text>
            </svg>
            <div className="min-w-[180px] text-left text-base font-medium">
                {items.map(item => (
                    <div
                        key={item.title}
                        className="mb-5 px-3 py-2"
                        style={{
                            borderRight: `2px solid ${item.color}`,
                            background: `linear-gradient(90deg, #ffffff00 12.26%, ${item.background} 100%)`
                        }}
                    >
                        <div style={{ color: item.color }}>
                            {item.title}：{item.count}
                        </div>
                        {item.rate ? <div className="mt-2 text-xs text-black/40">转化率：{item.rate}</div> : null}
                    </div>
                ))}
            </div>
        </div>
    );
}
